/// The open-post view: the post itself (with author Edit/Delete and
/// developer-moderation Delete), the flat comment thread, and a
/// reply composer. Votes are optimistic — the displayed score is the
/// stored value plus a local adjustment; the authoritative count
/// arrives via the snapshot listener (the Cloud Function recomputes
/// it). Deleting the post (by its author or the developer) cascades
/// the comments+votes server-side and we navigate back to the list.

import { useEffect, useState } from 'react'
import type { User } from 'firebase/auth'
import {
  DEV_EMAIL,
  createComment,
  deleteComment,
  deletePost,
  getMyVote,
  getPostContactStatus,
  isFeedbackPost,
  setPostArchived,
  setVote,
  subscribeComments,
  subscribePost,
  updateComment,
  updatePost,
  voteAggregateDeltas,
  type DevComment,
  type DevPost,
  type VoteValue,
} from '../data/devBlog'
import { markInboxReadForPost, subscribeInbox, type InboxItem } from '../data/inbox'
import { Linkified } from '../util/linkify'
import { DevBlogByline } from './DevBlogByline'
import { DevBlogCommentRow } from './DevBlogCommentRow'
import { DevBlogComposer } from './DevBlogComposer'
import { DevBlogFeedbackHeader } from './DevBlogFeedbackHeader'
import { DevBlogVoteControl } from './DevBlogVoteControl'

export function DevBlogPostDetail(props: {
  postId: string
  user: User
  onBack: () => void
  /// Optional telemetry hooks. Undefined on the real "Talk to the
  /// developer" board (DevBlogPage doesn't pass them, so behavior there is
  /// unchanged) — the Tower Log page passes these to fire its own
  /// `towerlog_vote` / `towerlog_comment` GA events without this shared
  /// component needing to know anything about Tower Log.
  onVoteCast?: () => void
  onCommentPosted?: () => void
}) {
  const { postId, user } = props
  const isDeveloper = user.email === DEV_EMAIL && user.emailVerified === true

  const [post, setPost] = useState<DevPost | null | undefined>(undefined) // undefined = loading
  const [postErr, setPostErr] = useState<string | null>(null)
  const [comments, setComments] = useState<DevComment[]>([])
  // Developer-only: does this poster have a reply-notification email
  // on file? Drives the badge above the reply composer so the dev
  // knows the reply will email them. Boolean only; the address never
  // reaches the client.
  const [posterHasEmail, setPosterHasEmail] = useState(false)

  useEffect(() => {
    if (!isDeveloper) return
    let cancelled = false
    getPostContactStatus(postId)
      .then((has) => {
        if (!cancelled) setPosterHasEmail(has)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [postId, isDeveloper])

  const [editingPost, setEditingPost] = useState(false)
  const [confirmDeletePost, setConfirmDeletePost] = useState(false)

  const [myPostVote, setMyPostVote] = useState<VoteValue | null>(null)
  const [postScoreAdj, setPostScoreAdj] = useState(0)
  const [myCommentVotes, setMyCommentVotes] = useState<Record<string, VoteValue | null>>({})
  const [commentScoreAdj, setCommentScoreAdj] = useState<Record<string, number>>({})

  useEffect(() => {
    const unsubPost = subscribePost(postId, setPost, (e) => setPostErr(e.message))
    const unsubComments = subscribeComments(postId, setComments, (e) => setPostErr(e.message))
    return () => {
      unsubPost()
      unsubComments()
    }
  }, [postId])

  /// Phase 2: when this thread opens, sweep the user's inbox for
  /// any unread entries pointing at this post and stamp `readAt`.
  /// Uses a short-lived subscription (one snapshot, then unsub) so
  /// we don't keep a listener open just for marking-read.
  useEffect(() => {
    let cancelled = false
    let unsubbed = false
    const unsub = subscribeInbox({
      uid: user.uid,
      limit: 50,
      onChange: (items: InboxItem[]) => {
        if (cancelled || unsubbed) return
        unsubbed = true
        unsub()
        void markInboxReadForPost({ uid: user.uid, postId, items }).catch(() => {})
      },
      onError: () => {
        if (cancelled || unsubbed) return
        unsubbed = true
        unsub()
      },
    })
    return () => {
      cancelled = true
      if (!unsubbed) unsub()
    }
  }, [postId, user.uid])

  useEffect(() => {
    void getMyVote({ postId, voterUid: user.uid })
      .then(setMyPostVote)
      .catch(() => {})
  }, [postId, user.uid])

  const commentIdsKey = comments.map((c) => c.id).join(',')
  useEffect(() => {
    let cancelled = false
    void Promise.all(
      comments.map(
        async (c) => [c.id, await getMyVote({ postId, commentId: c.id, voterUid: user.uid }).catch(() => null)] as const,
      ),
    ).then((pairs) => {
      if (cancelled) return
      setMyCommentVotes((prev) => {
        const next = { ...prev }
        for (const [id, v] of pairs) next[id] = v
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [postId, user.uid, commentIdsKey])

  const votePost = async (next: VoteValue | null) => {
    const prev = myPostVote
    const d = voteAggregateDeltas(prev, next)
    setMyPostVote(next)
    setPostScoreAdj((x) => x + d.score)
    try {
      await setVote({ postId, voterUid: user.uid, value: next })
      props.onVoteCast?.()
    } catch {
      setMyPostVote(prev)
      setPostScoreAdj((x) => x - d.score)
    }
  }

  const voteComment = async (commentId: string, next: VoteValue | null) => {
    const prev = myCommentVotes[commentId] ?? null
    const d = voteAggregateDeltas(prev, next)
    setMyCommentVotes((m) => ({ ...m, [commentId]: next }))
    setCommentScoreAdj((m) => ({ ...m, [commentId]: (m[commentId] ?? 0) + d.score }))
    try {
      await setVote({ postId, commentId, voterUid: user.uid, value: next })
      props.onVoteCast?.()
    } catch {
      setMyCommentVotes((m) => ({ ...m, [commentId]: prev }))
      setCommentScoreAdj((m) => ({ ...m, [commentId]: (m[commentId] ?? 0) - d.score }))
    }
  }

  if (post === undefined && !postErr) {
    return <p className="p-6 text-sm text-slate-400">Loading…</p>
  }
  if (post === null) {
    return (
      <div className="space-y-3 p-6 text-sm text-slate-400">
        <p>This post was removed.</p>
        <button type="button" onClick={props.onBack} className="text-emerald-300 hover:underline">
          ← Back to all posts
        </button>
      </div>
    )
  }
  if (!post) {
    return (
      <div className="space-y-3 p-6 text-sm text-rose-300">
        <p>Couldn't load this post{postErr ? `: ${postErr}` : ''}.</p>
        <button type="button" onClick={props.onBack} className="text-emerald-300 hover:underline">
          ← Back to all posts
        </button>
      </div>
    )
  }

  const feedback = isFeedbackPost(post)
  // Feedback posts are immutable from clients (authorUid is empty
  // by design). The developer can still delete + archive — those
  // both flow through `canDeletePost` / `canArchivePost` below.
  const canEditPost = !feedback && post.authorUid === user.uid
  const canDeletePost = canEditPost || isDeveloper
  const isArchived = !!post.archivedAt
  const canArchivePost = isDeveloper
  const replyCount = post.commentCount

  return (
    <div className="space-y-4">
      <button type="button" onClick={props.onBack} className="text-xs text-emerald-300 hover:underline">
        ← Back to all posts
      </button>

      {isArchived && (
        <div className="rounded-md bg-slate-800/60 px-3 py-2 text-xs text-slate-300 ring-1 ring-slate-600/40">
          <span className="font-semibold uppercase tracking-wider text-slate-400">Archived</span> — this post is closed
          to new replies and votes.
        </div>
      )}

      <article
        className={
          'flex gap-3 rounded-lg p-4 ring-1 ' +
          (post.authorIsDeveloper
            ? 'border-l-4 border-emerald-500 bg-emerald-500/5 ring-emerald-400/20'
            : 'bg-slate-900/60 ring-white/10') +
          (isArchived ? ' opacity-80' : '')
        }
      >
        <DevBlogVoteControl
          score={post.score + postScoreAdj}
          myVote={myPostVote}
          disabled={isArchived}
          onVote={votePost}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            {feedback ? (
              <DevBlogFeedbackHeader
                rating={post.feedbackRating ?? 0}
                levelId={post.feedbackLevelId}
                createdAt={post.createdAt}
              />
            ) : (
              <DevBlogByline
                authorName={post.authorName}
                authorUid={post.authorUid}
                authorIsDeveloper={post.authorIsDeveloper}
                createdAt={post.createdAt}
                editedAt={post.editedAt}
              />
            )}
            {(canEditPost || canDeletePost || canArchivePost) && !editingPost && (
              <div className="flex shrink-0 items-center gap-2 text-[11px]">
                {canEditPost && (
                  <button type="button" onClick={() => setEditingPost(true)} className="text-slate-400 hover:text-white">
                    Edit
                  </button>
                )}
                {canArchivePost && !confirmDeletePost && (
                  <button
                    type="button"
                    onClick={() => {
                      void setPostArchived({ postId, archive: !isArchived })
                    }}
                    className="text-slate-400 hover:text-white"
                  >
                    {isArchived ? 'Unarchive' : 'Archive'}
                  </button>
                )}
                {canDeletePost && !confirmDeletePost && (
                  <button
                    type="button"
                    onClick={() => setConfirmDeletePost(true)}
                    className="text-rose-400 hover:text-rose-300"
                  >
                    Delete
                  </button>
                )}
                {canDeletePost && confirmDeletePost && (
                  <>
                    <span className="text-slate-500">Delete this post and all replies?</span>
                    <button
                      type="button"
                      onClick={async () => {
                        await deletePost(postId)
                        props.onBack()
                      }}
                      className="text-rose-400 hover:text-rose-300"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeletePost(false)}
                      className="text-slate-400 hover:text-white"
                    >
                      No
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          {editingPost ? (
            <div className="mt-2">
              <DevBlogComposer
                kind="post"
                initialTitle={post.title}
                initialBody={post.body}
                submitLabel="Save"
                autoFocus
                onCancel={() => setEditingPost(false)}
                onSubmit={async ({ title, body }) => {
                  await updatePost({ postId, title, body })
                  setEditingPost(false)
                }}
              />
            </div>
          ) : (
            <>
              <h2 className="mt-1 text-base font-semibold text-white">{post.title}</h2>
              <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-slate-200">
                <Linkified text={post.body} />
              </p>
            </>
          )}
        </div>
      </article>

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {replyCount === 1 ? '1 reply' : `${replyCount} replies`}
        </h3>
        {comments.map((c) => (
          <DevBlogCommentRow
            key={c.id}
            comment={{ ...c, score: c.score + (commentScoreAdj[c.id] ?? 0) }}
            myVote={myCommentVotes[c.id] ?? null}
            canEdit={c.authorUid === user.uid}
            canDelete={c.authorUid === user.uid || isDeveloper}
            voteDisabled={isArchived}
            onVote={(next) => voteComment(c.id, next)}
            onEdit={(body) => updateComment({ postId, commentId: c.id, body })}
            onDelete={() => deleteComment({ postId, commentId: c.id })}
          />
        ))}
        {comments.length === 0 && <p className="text-xs text-slate-500">No replies yet.</p>}
      </section>

      {!isArchived ? (
        <section className="rounded-lg bg-slate-900/40 p-3 ring-1 ring-white/5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Add a reply</h3>
          {posterHasEmail && (
            <p className="mb-2 rounded-md bg-emerald-900/40 px-3 py-2 text-xs text-emerald-100 ring-1 ring-emerald-500/30">
              📧 This poster left an email. Your reply sends them a notification.
            </p>
          )}
          <DevBlogComposer
            kind="comment"
            submitLabel="Reply"
            onSubmit={async ({ body }) => {
              await createComment({ postId, author: user, body })
              props.onCommentPosted?.()
            }}
          />
        </section>
      ) : (
        <p className="text-xs text-slate-500">Replies are closed on this post.</p>
      )}
    </div>
  )
}
